import React, { useState, useEffect } from 'react';
import { 
  ChevronDown, 
  MapPin, 
  Calendar, 
  Users, 
  Fish, 
  Camera, 
  Award,
  Menu,
  X,
  Mail,
  Phone,
  Trophy,
  Target,
  Waves,
  Clock,
  DollarSign,
  Star,
  Zap,
  ArrowRight,
  Home,
  UserPlus,
  Images
} from 'lucide-react';
import YakaBassLogo from './components/YakaBassLogo';
import { NavButton } from './components/NavButton';
import TournamentStandings from './components/TournamentStandings';

const App: React.FC = () => {
  const [currentHeroImage, setCurrentHeroImage] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [showCompleteGallery, setShowCompleteGallery] = useState(false);
  
  // Hero image rotation every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeroImage((prev) => (prev + 1) % 3);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Parallax scroll effect
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Hero background images - Using your actual photos
  const heroImages = [
    '/images/hero/hero1.png',
    '/images/hero/hero2.png', 
    '/images/hero/hero3.png'
  ];

  const tournaments = [
    {
      date: 'March 15, 2024',
      title: 'Spring Bass Classic',
      location: 'Lake Berryessa',
      description: 'Kick off the season with our annual Spring Bass Classic. Perfect conditions expected!',
      entryFee: '$75',
      maxParticipants: 200
    },
    {
      date: 'April 20, 2024',
      title: 'Delta Challenge',
      location: 'Sacramento Delta',
      description: 'Navigate the challenging waters of the Delta for trophy bass in this premier event.',
      entryFee: '$85',
      maxParticipants: 200
    },
    {
      date: 'May 25, 2024',
      title: 'Clear Lake Open',
      location: 'Clear Lake',
      description: 'California\'s oldest natural lake hosts this exciting tournament with great prizes.',
      entryFee: '$80',
      maxParticipants: 200
    }
  ];

  // Function to check if tournament date has passed
  const isTournamentCompleted = (dateString: string) => {
    const tournamentDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
    return tournamentDate < today;
  };

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 rugged-masculine">
      {/* Background Mesh */}
      <div className="fixed inset-0 bg-mesh opacity-50 pointer-events-none" />
      
      {/* Navigation */}
      <nav className="fixed top-0 w-full glass-nav z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-18">
            {/* Logo and Brand */}
            <div className="flex items-center space-x-4 py-2">
              <YakaBassLogo className="w-14 h-14" showText={true} />
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-3 nav-container">
              {[
                { id: 'home', label: 'Home', icon: Home },
                { id: 'tournaments', label: 'Tournaments', icon: Trophy },
                { id: 'membership', label: 'Membership', icon: UserPlus },
                { id: 'gallery', label: 'Gallery', icon: Images },
                { id: 'contact', label: 'Contact', icon: Phone }
              ].map((section, index) => {
                const IconComponent = section.icon;
                return (
                  <NavButton
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    delay={index * 0.1}
                  >
                    <IconComponent className="w-4 h-4 group-hover:scale-110 transition-transform duration-300" />
                    <span className="capitalize">{section.label}</span>
                  </NavButton>
                );
              })}
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-slate-800 hover:text-slate-600 transition-colors p-2 rounded-lg hover:bg-slate-100"
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          {isMenuOpen && (
            <div className="md:hidden glass-card rounded-t-none border-t-0 bg-slate-50/95">
              <div className="px-4 py-6 space-y-3">
                {['home', 'tournaments', 'membership', 'gallery', 'contact'].map((section) => (
                  <button
                    key={section}
                    onClick={() => scrollToSection(section)}
                    className="block w-full text-left px-4 py-3 text-slate-800 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors capitalize"
                  >
                    {section}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative h-screen overflow-hidden">
        {heroImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentHeroImage ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div
              className="hero-bg w-full h-full bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url('${image}')`,
                transform: `translateY(${scrollY * 0.5}px)`,
              }}
            />
          </div>
        ))}
        
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <div className="text-center text-white px-4 max-w-6xl">
            <div className="floating mb-8">
              <h1 className="text-6xl md:text-8xl font-black mb-6 text-shadow masculine-heading">
                <span className="text-slate-100">
                  YAK-A-BASS
                </span>
              </h1>
            </div>
            <div className="animate-slideInUp">
              <p className="text-2xl md:text-4xl mb-4 font-light text-shadow">
                Northern California
              </p>
              <p className="text-xl md:text-2xl mb-12 text-cyan-200 font-medium">
                Premier Kayak Bass Fishing Tournament Organization
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                <button 
                  onClick={() => scrollToSection('membership')}
                  className="group relative px-8 py-4 bg-gradient-to-r from-cyan-500 to-emerald-600 text-white font-bold text-lg rounded-xl shadow-2xl hover:shadow-cyan-500/25 transition-all duration-300 transform hover:scale-105 neon-glow premium-cta floating-action"
                >
                  <span className="flex items-center gap-2">
                    Join Elite Anglers
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </button>
                <button 
                  onClick={() => scrollToSection('tournaments')}
                  className="px-8 py-4 glass-card text-white font-semibold text-lg rounded-xl hover:bg-white/20 transition-all duration-300 dark-optimized"
                >
                  View Standings
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
          <ChevronDown 
            className="w-8 h-8 text-white animate-bounce cursor-pointer pulse-glow" 
            onClick={() => scrollToSection('about')} 
          />
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 px-4 relative">
        <div className="container mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-black text-white mb-8">
              Elite Kayak Bass
              <span className="gradient-text block">Community</span>
            </h2>
            <p className="text-xl md:text-2xl text-white/80 max-w-4xl mx-auto leading-relaxed">
              Where Northern California's most dedicated kayak bass anglers compete, 
              connect, and push the boundaries of the sport.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                icon: <Trophy className="w-12 h-12" />,
                title: "Elite Competition",
                description: "Battle against NorCal's top kayak bass anglers in professionally organized tournaments across premium waters.",
                gradient: "from-yellow-400 to-orange-500"
              },
              {
                icon: <Users className="w-12 h-12" />,
                title: "Exclusive Network",
                description: "Connect with passionate anglers, share cutting-edge techniques, and build lasting relationships in our tight-knit community.",
                gradient: "from-cyan-400 to-blue-500"
              },
              {
                icon: <Target className="w-12 h-12" />,
                title: "Championship Level",
                description: "Fish against some of the best anglers in the country. Grow, learn, win, and if not, try again.",
                gradient: "from-blue-400 to-emerald-500"
              }
            ].map((feature, index) => (
              <div key={index} className="group">
                <div className="morphism-card p-8 rounded-2xl card-hover-3d interactive-card h-full deep-shadow">
                  <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${feature.gradient} mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <div className="text-white">
                      {feature.icon}
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">{feature.title}</h3>
                  <p className="text-white/70 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tournaments Section */}
      <section id="tournaments" className="py-32 px-4 relative">
        <div className="container mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-black text-white mb-8">
              Tournament
              <span className="gradient-text block">Central</span>
            </h2>
            <p className="text-xl text-white/80">Live standings and championship events</p>
          </div>

          {/* Tournament Standings */}
          <div className="mb-24">
            <TournamentStandings />
          </div>

          {/* Upcoming Tournaments */}
          <div className="text-center mb-16">
            <h3 className="text-4xl font-black text-white mb-4">
              <Zap className="inline w-10 h-10 mr-3 text-yellow-400" />
              Championship Events
            </h3>
            <p className="text-lg text-white/70">Compete for glory in California's premier kayak bass tournaments</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {tournaments.map((tournament, index) => {
              const isCompleted = isTournamentCompleted(tournament.date);
              return (
                <div key={index} className="group">
                  <div className="tournament-card p-8 rounded-2xl card-hover-3d interactive-card h-full text-white deep-shadow">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center space-x-3">
                        <Calendar className="w-6 h-6 text-cyan-400" />
                        <span className="text-cyan-200 font-semibold">{tournament.date}</span>
                      </div>
                      <Star className="w-6 h-6 text-yellow-400" />
                    </div>
                    
                    <h3 className="text-2xl font-bold mb-4 group-hover:text-cyan-300 transition-colors masculine-heading">{tournament.title}</h3>
                    
                    <div className="flex items-center mb-6">
                      <MapPin className="w-5 h-5 text-blue-400 mr-3" />
                      <span className="text-white/80">{tournament.location}</span>
                    </div>
                    
                    <p className="text-white/70 mb-8 leading-relaxed">
                      {tournament.description}
                    </p>
                    
                    <div className="flex items-center justify-between mb-8">
                      <div className="flex items-center text-emerald-400 font-semibold">
                        <DollarSign className="w-5 h-5 mr-1" />
                        {tournament.entryFee}
                      </div>
                      <div className="flex items-center text-cyan-400">
                        <Users className="w-5 h-5 mr-2" />
                        <span className="text-sm">Max {tournament.maxParticipants}</span>
                      </div>
                    </div>
                    
                    {isCompleted ? (
                      <button className="w-full bg-gradient-to-r from-slate-600 to-slate-700 text-white font-bold py-4 px-6 rounded-xl transition-all duration-300 cursor-not-allowed opacity-75">
                        <span className="flex items-center justify-center gap-2">
                          <Award className="w-4 h-4" />
                          Event Completed
                        </span>
                      </button>
                    ) : (
                      <button className="w-full bg-gradient-to-r from-cyan-500 to-emerald-600 hover:from-cyan-400 hover:to-emerald-500 text-white font-bold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-cyan-500/25 premium-cta floating-action">
                        <span className="flex items-center justify-center gap-2">
                          Secure Your Spot
                          <ArrowRight className="w-4 h-4" />
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Membership Section */}
      <section id="membership" className="py-32 px-4 relative">
        <div className="container mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-black text-white mb-8">
              Join The
              <span className="gradient-text block">Elite</span>
            </h2>
            <p className="text-xl text-white/80">Become part of NorCal's most exclusive kayak bass fishing community</p>
          </div>

          <div className="grid md:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
            <div className="space-y-8">
              <h3 className="text-3xl font-bold text-white mb-8">Elite Member Benefits</h3>
              
              {[
                {
                  icon: <Trophy className="w-6 h-6" />,
                  title: "Tournament Access",
                  description: "Exclusive entry to all championship events and member-only competitions"
                },
                {
                  icon: <Users className="w-6 h-6" />,
                  title: "Expert Network",
                  description: "Private community access with pro tips, techniques, and mentorship"
                },
                {
                  icon: <MapPin className="w-6 h-6" />,
                  title: "Premium Locations",
                  description: "Access to exclusive launch sites and private tournament waters"
                },
                {
                  icon: <Award className="w-6 h-6" />,
                  title: "Pro Discounts",
                  description: "Partner discounts on gear, equipment, and tournament fees"
                },
                {
                  icon: <Calendar className="w-6 h-6" />,
                  title: "VIP Events", 
                  description: "Annual banquet, skills workshops, and exclusive social gatherings"
                }
              ].map((benefit, index) => (
                <div key={index} className="flex items-start space-x-4 group">
                  <div className="flex-shrink-0 p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg group-hover:scale-110 transition-transform">
                    <div className="text-white">
                      {benefit.icon}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-1">{benefit.title}</h4>
                    <p className="text-white/70">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center">
              <div className="morphism-card p-10 rounded-3xl border-gradient">
                <div className="mb-8">
                  <Star className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
                  <h3 className="text-3xl font-bold text-white mb-4">Elite Membership</h3>
                  <p className="text-white/80 mb-8">
                    Complete access to tournaments, community, and exclusive member benefits
                  </p>
                </div>
                
                <div className="mb-8">
                  <div className="text-6xl font-black text-white mb-2">
                    $65
                    <span className="text-2xl font-normal text-white/60"> /year</span>
                  </div>
                  <p className="text-emerald-400 font-semibold">All tournaments included</p>
                </div>
                
                <button className="w-full bg-gradient-to-r from-cyan-500 to-emerald-600 hover:from-cyan-400 hover:to-emerald-500 text-white font-bold text-xl py-6 px-8 rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-cyan-500/25 mb-4 neon-glow premium-cta floating-action">
                  <span className="flex items-center justify-center gap-3">
                    <Zap className="w-6 h-6" />
                    Join Elite Anglers
                    <ArrowRight className="w-6 h-6" />
                  </span>
                </button>
                
                <p className="text-sm text-white/60">
                  New member orientation and welcome package included
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-32 px-4 relative">
        <div className="container mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-black text-white mb-8">
              Trophy
              <span className="gradient-text block">Gallery</span>
            </h2>
            <p className="text-xl text-white/80">Epic catches and championship moments from our elite community</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {/* Using all unique fish photos from yab folder */}
            {(() => {
              const allImages = [
                '/images/gallery/fish1.webp',
                '/images/gallery/fish2.webp', 
                '/images/gallery/fish3.webp',
                '/images/gallery/fish4.webp',
                '/images/gallery/fish6.webp',
                '/images/gallery/fish8.webp',
                '/images/gallery/fish9.webp',
                '/images/gallery/fish10.webp',
                '/images/gallery/fish11.webp',
                '/images/gallery/fish12.webp',
                '/images/gallery/fish16.webp',
                '/images/gallery/fish17.webp',
                '/images/gallery/fish18.webp',
                '/images/gallery/fish19.webp',
                '/images/gallery/fish20.webp',
              ];
              const displayImages = showCompleteGallery ? allImages : allImages.slice(0, 12);
              return displayImages.map((imageSrc, index) => (
              <div key={index} className="group">
                <div className="aspect-square morphism-card rounded-2xl overflow-hidden card-hover-3d">
                  <img 
                    src={imageSrc}
                    alt={`Championship catch ${index + 1}`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-all duration-500 image-glow"
                    onError={(e) => {
                      // Fallback to placeholder if image fails to load
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      target.parentElement!.innerHTML = `
                        <div class="w-full h-full flex items-center justify-center text-white/60">
                          <div class="text-center">
                            <div class="w-12 h-12 mx-auto mb-2 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
                              <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                              </svg>
                            </div>
                            <p class="text-sm font-medium">Trophy Catch</p>
                          </div>
                        </div>
                      `;
                    }}
                  />
                </div>
              </div>
              ));
            })()}
          </div>

          <div className="text-center mt-12">
            <button 
              onClick={() => setShowCompleteGallery(!showCompleteGallery)}
              className="glass-card px-8 py-4 text-white font-semibold text-lg rounded-xl hover:bg-white/20 transition-all duration-300"
            >
              <span className="flex items-center gap-2">
                <Camera className="w-5 h-5" />
                {showCompleteGallery ? 'Show Less' : 'View Complete Gallery'}
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 px-4 relative">
        <div className="container mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-black text-white mb-8">
              Community
              <span className="gradient-text block">Spotlight</span>
            </h2>
            <p className="text-xl text-white/80">Featuring our amazing partners and supporters</p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 mb-16">
              <div className="morphism-card p-8 rounded-2xl text-center card-hover-3d">
                <div className="inline-flex p-4 bg-gradient-to-br from-emerald-500 to-cyan-600 rounded-2xl mb-6">
                  <Trophy className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Tournament Director</h3>
                <p className="text-white/70 mb-4 text-lg">James Snyder</p>
                <a href="mailto:james@yakabass.com" className="text-emerald-400 hover:text-emerald-300 font-semibold text-lg transition-colors">
                  james@yakabass.com
                </a>
              </div>
              
              <div className="morphism-card p-8 rounded-2xl text-center card-hover-3d">
                <div className="inline-flex p-4 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl mb-6">
                  <Mail className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Technology Director</h3>
                <p className="text-white/70 mb-4 text-lg">Ryan Bolden</p>
                <a href="mailto:ryan@yakabass.com" className="text-cyan-400 hover:text-cyan-300 font-semibold text-lg transition-colors">
                  ryan@yakabass.com
                </a>
              </div>
            </div>

            <div className="morphism-card p-10 rounded-3xl">
              <h3 className="text-3xl font-bold text-white text-center mb-10">Member Spotlight</h3>
              
              <div className="grid md:grid-cols-10 gap-8 items-center">
                {/* Text Content - 30% */}
                <div className="md:col-span-3 space-y-6">
                  <div className="text-center md:text-left">
                    <h4 className="text-2xl font-bold text-cyan-400 mb-4">FluxJet Kayaks</h4>
                    <p className="text-white/80 text-lg leading-relaxed mb-6">
                      The most revolutionary kayak company in the industry! FluxJet is pioneering the future of kayak bass fishing with their cutting-edge designs and innovative technology.
                    </p>
                    <p className="text-emerald-400 font-semibold text-lg mb-4">
                      📍 Located right here in Sacramento, CA!
                    </p>
                    <p className="text-white/70 leading-relaxed">
                      As proud supporters of the Northern California bass fishing community, FluxJet continues to push the boundaries of what's possible on the water. Their revolutionary kayak designs are changing the game for serious anglers.
                    </p>
                  </div>
                </div>
                
                {/* YouTube Video - 70% */}
                <div className="md:col-span-7">
                  <div className="relative aspect-video rounded-xl overflow-hidden shadow-2xl border border-cyan-500/20">
                    <iframe
                      src="https://www.youtube.com/embed/TwxvBkou7i8"
                      title="FluxJet Kayaks - Revolutionary Design"
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    ></iframe>
                  </div>
                  <p className="text-center text-white/60 text-sm mt-4">
                    Experience the FluxJet revolution in kayak bass fishing
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-4 relative border-t border-slate-200 bg-slate-50">
        <div className="container mx-auto">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-4 mb-8">
              <YakaBassLogo className="w-16 h-16" showText={false} />
              <div>
                <h3 className="text-2xl font-bold text-slate-800">YAK-A-BASS NOR-CAL</h3>
                <p className="text-slate-600">Elite Kayak Bass Championship Organization</p>
              </div>
            </div>
            
            <div className="border-t border-slate-200 pt-8">
              <p className="text-slate-600 mb-2">
                © 2024 YAK-A-BASS NOR-CAL. All rights reserved.
              </p>
              <p className="text-slate-500">
                <span className="gradient-text font-semibold">Tight lines and championship dreams!</span>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;